import {
  Badge,
  Button,
  CloseMdIcon,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTitle,
  IconButton,
  PortalContainerProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { useConfirmModal } from 'context/confirmModal';
import { FormEvent, ReactNode, useState } from 'react';
import { cn } from 'utils/style-utils';

// DrawerBody's own class lands on the content inside its scroll area, so the
// padding of the scroll root and viewport is cleared from the element around
// it: the sections draw their dividers edge to edge.
const DRAWER_BODY_FLUSH =
  '[&>div:has(>[data-id$=-viewport])]:p-0 [&>div>[data-id$=-viewport]]:p-0';

export interface CreationDrawerProps {
  title: string;
  /** Shown as a badge next to the title. */
  count?: number;
  submitLabel?: string;
  /**Blocks submit and explains why on hover. */
  submitDisabledReason?: string | null;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  /** Unsaved changes: closing the drawer asks before dropping them. */
  isDirty: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Right-side drawer shared by the creation forms: title, scrollable sections
 * and a Cancel / submit footer. Closing it with unsaved changes asks the user
 * to continue editing or discard them; while submitting it can't be closed
 * and its fields can't be changed.
 */
const CreationDrawer = ({
  title,
  count,
  submitLabel = 'Add',
  submitDisabledReason,
  submitDisabled,
  isSubmitting,
  isDirty,
  onSubmit,
  onClose,
  children,
}: CreationDrawerProps) => {
  const { openModal } = useConfirmModal();
  const [drawerEl, setDrawerEl] = useState<HTMLElement | null>(null);

  const isSubmitBlocked = !!(submitDisabled || submitDisabledReason);

  const requestClose = () => {
    if (!isDirty) {
      onClose();
      return;
    }

    openModal({
      type: 'info',
      title: 'Discard your changes?',
      text: 'You have unsaved changes. If you close now, they will be lost.',
      confirmText: 'Continue editing',
      cancelText: 'Discard changes',
      confirmFunction: () => undefined,
      cancelFunction: onClose,
      disableClickOnBackground: true,
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || isSubmitBlocked) return;
    onSubmit();
  };

  return (
    // Swipe-to-dismiss captures the pointer on anything that isn't a native
    // control, which swallows clicks on the menus and switches inside.
    <Drawer
      open
      onOpenChange={(next, { reason, event }) => {
        // Closing mid-submit would drop the steps the form runs after the save.
        if (next || isSubmitting) return;
        // A picker inside (owner, select, sequence) closes on the same Escape
        // first and marks it handled; that press is for the picker, not the drawer.
        if (reason === 'escape-key' && event.defaultPrevented) return;
        // A quick second click on an open select's trigger lands on the page
        // root while the select locks the page; a real click outside the
        // drawer lands on the layer its portal puts around it.
        if (
          reason === 'outside-press' &&
          !(event.target as Element | null)?.closest?.(
            '[data-slot="drawer-portal"]',
          )
        ) {
          return;
        }
        requestClose();
      }}
      side="right"
      disableSwipeDismissal
    >
      <DrawerContent className="w-107">
        {/* Popups portalled to the body would land outside the modal drawer,
            whose focus management reads them as focus-outside and closes them. */}
        <PortalContainerProvider container={drawerEl}>
          <div
            ref={(element) =>
              setDrawerEl(
                (element?.closest(
                  '[data-slot="drawer-content"]',
                ) as HTMLElement) ?? null,
              )
            }
            hidden
          />

          <form
            noValidate
            onSubmit={handleSubmit}
            className={cn('flex-1 min-h-0 flex flex-col', DRAWER_BODY_FLUSH)}
          >
            {/* DrawerHeader's own close button can't be disabled while submitting. */}
            <div className="flex items-center gap-6 p-4">
              <DrawerClose
                disabled={isSubmitting}
                render={
                  <IconButton
                    variant="light"
                    tone="lightGray"
                    shape="rounded"
                    label="Close drawer"
                    icon={CloseMdIcon}
                  />
                }
              />
              <div className="flex-1 flex items-center gap-3">
                <DrawerTitle>{title}</DrawerTitle>
                {count !== undefined && (
                  <Badge tone="darkGray" shape="pill" size="lg">
                    {count}
                  </Badge>
                )}
              </div>
            </div>

            <DrawerBody className="gap-0">
              {/* The fieldset disables the native controls; `inert` also stops
                  the ones that aren't (radios, menu cards, legacy pickers).
                  React 18 has no `inert` prop, hence the spread. */}
              <fieldset
                disabled={isSubmitting}
                {...(isSubmitting && { inert: '' })}
                className="m-0 p-0 border-0 min-w-0 flex flex-col divide-y divide-neutral-300"
              >
                {children}
              </fieldset>
            </DrawerBody>

            <DrawerFooter className="sm:justify-stretch pt-3 pb-6 px-6">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="w-30"
                disabled={isSubmitting}
                onClick={requestClose}
              >
                Cancel
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled button fires no pointer events, so the reason
                      hangs off a wrapper around it. */}
                  <span className="w-30 flex">
                    <Button
                      type="submit"
                      size="md"
                      className="flex-1"
                      loading={isSubmitting}
                      disabled={isSubmitBlocked}
                    >
                      {submitLabel}
                    </Button>
                  </span>
                </TooltipTrigger>
                {submitDisabledReason && (
                  <TooltipContent>{submitDisabledReason}</TooltipContent>
                )}
              </Tooltip>
            </DrawerFooter>
          </form>
        </PortalContainerProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default CreationDrawer;
